import { Link, router } from "@inertiajs/react";
import type Category from "@/types/serializers/Category";
import { useFlashToast } from "@/hooks/useFlashToast";
import { useIndexNavigation, useSyncedFilter } from "@/hooks/useIndexNavigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Plus,
  Search,
  X,
} from "lucide-react";

type Props = {
  categories: Category[];
  total: number;
  page: { size: number; number: number };
  totalPages: number;
  flash?: { notice?: string; alert?: string };
};

function SortIcon({ direction }: { direction: "asc" | "desc" | null }) {
  if (direction === "asc") return <ArrowUp className="size-[11.2px]" />;
  if (direction === "desc") return <ArrowDown className="size-[11.2px]" />;
  return <ArrowUpDown className="size-[11.2px] text-muted-foreground" />;
}

export default function CategoriesIndex({
  categories,
  total,
  page,
  totalPages,
  flash,
}: Props) {
  useFlashToast(flash);
  const navigation = useIndexNavigation("/categories", {
    only: ["categories", "total", "totalPages", "page"],
  });
  const { navigate, handleSort, sortDirection, pagination } = navigation;
  const [filterName, setFilterName] = useSyncedFilter("filter_name", navigation);
  const [filterDescription, setFilterDescription] = useSyncedFilter("filter_description", navigation);

  function clearFilters() {
    navigate({ filter_name: null, filter_description: null, offset: null });
  }

  const hasFilters = filterName || filterDescription;
  const { currentPage, canPrev, canNext } = pagination(page, totalPages);

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Categories</h1>
          <p className="text-sm text-muted-foreground">
            {total} {total === 1 ? "category" : "categories"} total
          </p>
        </div>
        <Button asChild>
          <Link href="/categories/new">
            <Plus className="size-4" />
            New Category
          </Link>
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Input
            placeholder="Filter by name…"
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
            style={{ paddingLeft: "30px" }}
            className="pl-[40px]"
          />
          <Search className="pointer-events-none absolute left-2.5 top-0 h-9 size-[16.7px] text-muted-foreground" />
        </div>
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Input
            placeholder="Filter by description…"
            value={filterDescription}
            style={{ paddingLeft: "30px" }}
            onChange={(e) => setFilterDescription(e.target.value)}
            className="pl-[40px]"
          />
          <Search className="pointer-events-none absolute left-2.5 top-0 h-9 size-[16.7px] text-muted-foreground" />
        </div>
        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            <X className="size-4" />
            Clear
          </Button>
        )}
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[60px]">
                <button
                  className="inline-flex items-center gap-1"
                  onClick={() => handleSort("id")}
                >
                  ID <SortIcon direction={sortDirection("id")} />
                </button>
              </TableHead>
              <TableHead>
                <button
                  className="inline-flex items-center gap-1"
                  onClick={() => handleSort("name")}
                >
                  Name <SortIcon direction={sortDirection("name")} />
                </button>
              </TableHead>
              <TableHead className="hidden md:table-cell">
                <button
                  className="inline-flex items-center gap-1"
                  onClick={() => handleSort("description")}
                >
                  Description <SortIcon direction={sortDirection("description")} />
                </button>
              </TableHead>
              <TableHead className="w-[120px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {categories.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="h-24 text-center text-muted-foreground">
                  No categories found.
                </TableCell>
              </TableRow>
            ) : (
              categories.map((category) => (
                <TableRow key={category.id}>
                  <TableCell className="font-mono text-muted-foreground">
                    {category.id}
                  </TableCell>
                  <TableCell className="font-medium">
                    <Link
                      href={`/categories/${category.id}`}
                      className="hover:underline"
                    >
                      {category.name}
                    </Link>
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-muted-foreground">
                    {category.description ? (
                      <span className="line-clamp-1">{category.description}</span>
                    ) : (
                      <span className="italic">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" asChild>
                        <Link href={`/categories/${category.id}`}>View</Link>
                      </Button>
                      <Button variant="ghost" size="sm" asChild>
                        <Link href={`/categories/${category.id}/edit`}>Edit</Link>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => {
                          if (confirm("Are you sure you want to delete this category?"))
                            router.delete(`/categories/${category.id}`);
                        }}
                      >
                        Delete
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Rows per page</span>
            <Select
              value={String(page.size)}
              onValueChange={(value) =>
                navigate({ page_size: value, offset: null })
              }
            >
              <SelectTrigger className="w-[70px] h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[10, 15, 25].map((size) => (
                  <SelectItem key={size} value={String(size)}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              Page {currentPage} of {totalPages}
            </span>
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="icon"
                className="size-8"
                disabled={!canPrev}
                onClick={() =>
                  navigate({ offset: String(page.number - page.size) })
                }
              >
                <ChevronLeft className="size-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="size-8"
                disabled={!canNext}
                onClick={() =>
                  navigate({ offset: String(page.number + page.size) })
                }
              >
                <ChevronRight className="size-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
